import { useId, useState } from 'react';
import type { EloPoint } from '@edh/shared';
import { RANKS, RANK_BAND, display, rankFor } from '@edh/shared';

/**
 * A rating over the games that made it.
 *
 * The x axis is games, not time. A rating only moves when somebody plays, so
 * spacing the points by the clock would draw a busy Tuesday as a single vertical
 * cliff and the fortnight after it as a long flat plain — a picture of the
 * calendar rather than of the rating. One step per game says the thing the chart
 * is for: how it got here.
 *
 * The placement games are drawn provisionally, because they were. They swing at
 * double K and are not a rating yet, and a line that renders them identically to
 * the rest is claiming a steadiness the number did not have.
 */
export function EloChart({
  series,
  color,
  bands,
  label,
}: {
  series: EloPoint[];
  /** The line's colour. A pilot's rank, or the gold everything else uses. */
  color: string;
  /** Tint the rank ladder behind it. Pilots have ranks; decks have brackets. */
  bands?: boolean;
  label: string;
}) {
  const clip = useId().replace(/:/g, '');
  const [at, setAt] = useState<number | null>(null);

  // One point is a rating, not a history. Nothing to draw a line between.
  if (series.length < 2) return null;

  const W = 320;
  const H = 132;
  const LEFT = 4;
  const RIGHT = 4;
  const TOP = 8;
  /** Room under the plot for the dates. */
  const AXIS = 16;

  const values = series.map((p) => p.elo);
  let hi = Math.max(...values);
  let lo = Math.min(...values);
  /**
   * A floor on the span, so a rating that has barely moved is drawn as a rating
   * that has barely moved. Without it, six points inside four rating points get
   * stretched to fill the box and a quiet month looks like a rollercoaster.
   */
  const MIN_SPAN = 80;
  if (hi - lo < MIN_SPAN) {
    const mid = (hi + lo) / 2;
    hi = mid + MIN_SPAN / 2;
    lo = mid - MIN_SPAN / 2;
  } else {
    const pad = (hi - lo) * 0.16;
    hi += pad;
    lo -= pad;
  }

  const x = (i: number) => LEFT + (i / (series.length - 1)) * (W - LEFT - RIGHT);
  const y = (elo: number) => TOP + (1 - (elo - lo) / (hi - lo)) * (H - TOP - AXIS);

  /** Every hundred inside the view — which is exactly one rank apiece. */
  const lines: number[] = [];
  for (let v = Math.ceil(lo / RANK_BAND) * RANK_BAND; v < hi; v += RANK_BAND) lines.push(v);

  const at0 = series[0]!;
  const last = series[series.length - 1]!;
  const cursor = at ?? series.length - 1;
  const here = series[cursor]!;

  /** The provisional run is a prefix, so one index splits the line in two. */
  const split = series.reduce((idx, p, i) => (p.placement ? i : idx), 0);
  const points = (from: number, to: number) =>
    series
      .slice(from, to + 1)
      .map((p, i) => `${x(from + i)},${y(p.elo)}`)
      .join(' ');

  /**
   * The high-water mark, marked only when it is somewhere other than here.
   * A ring around the last point saying "this is the highest it has been" is a
   * decoration on a fact the number above it already gives you.
   */
  const peakAt = values.reduce((best, v, i) => (v > values[best]! ? i : best), 0);
  const showPeak = values[peakAt]! > last.elo + 1 && peakAt > 0;

  return (
    <div className="elo-chart-wrap">
      <svg
        className="elo-chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={label}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          scrub(e.clientX, e.currentTarget);
        }}
        onPointerMove={(e) => {
          if (e.buttons > 0 || e.pointerType === 'mouse') scrub(e.clientX, e.currentTarget);
        }}
        onPointerLeave={() => setAt(null)}
      >
        <defs>
          {/*
            The reveal. Set to full width as an attribute and animated down from
            zero in CSS, so a browser that will not animate an SVG geometry
            property simply shows the finished chart.
          */}
          <clipPath id={clip}>
            <rect className="elo-chart-reveal" x="0" y="0" width={W} height={H} />
          </clipPath>
        </defs>

        {/* The ladder, behind everything. Colour carries the rank here exactly
            as it does on the halo, so the climb is legible before the axis is
            read. */}
        {bands &&
          RANKS.map((rank, i) => {
            const floor = Number.isFinite(rank.floor) ? rank.floor : lo;
            const ceiling = RANKS[i + 1]?.floor ?? hi;
            if (ceiling <= lo || floor >= hi) return null;
            const top = Math.min(hi, ceiling);
            const bottom = Math.max(lo, floor);
            return (
              <rect
                key={rank.name}
                x={0}
                y={y(top)}
                width={W}
                height={Math.max(0, y(bottom) - y(top))}
                fill={rank.color}
                opacity={0.07}
              />
            );
          })}

        {lines.map((v) => (
          <g key={v}>
            <line x1={LEFT} y1={y(v)} x2={W - RIGHT} y2={y(v)} className="elo-chart-grid" />
            <text
              className="elo-chart-scale"
              x={LEFT + 2}
              y={y(v) - 3}
              fill={bands ? rankFor(v).color : undefined}
            >
              {bands ? rankFor(v).name : v}
            </text>
          </g>
        ))}

        <line x1={x(cursor)} y1={TOP} x2={x(cursor)} y2={H - AXIS} className="elo-chart-cursor" />

        <g clipPath={`url(#${clip})`}>
          {/* Placement first, so the solid line laps over its end point rather
              than the dashes cutting into the real one. */}
          {split > 0 && (
            <polyline className="elo-chart-line provisional" points={points(0, split)} stroke={color} />
          )}
          {split < series.length - 1 && (
            <polyline
              className="elo-chart-line"
              points={points(split, series.length - 1)}
              stroke={color}
            />
          )}

          {series.map((p, i) =>
            i === 0 ? null : (
              <circle
                key={p.matchId ?? i}
                className={`elo-chart-dot${p.won ? ' won' : ''}`}
                cx={x(i)}
                cy={y(p.elo)}
                r={p.won ? 3 : 2.2}
                fill={p.won ? color : 'var(--panel)'}
                stroke={color}
              />
            ),
          )}

          {showPeak && (
            <circle className="elo-chart-peak" cx={x(peakAt)} cy={y(values[peakAt]!)} r={5.5} />
          )}
        </g>

        <circle className="elo-chart-here" cx={x(cursor)} cy={y(here.elo)} r={4} fill={color} />

        {/* Both ends only. Every date would be unreadable at this width, and the
            two that matter are where it started and where it is. */}
        <text className="elo-chart-tick" x={LEFT} y={H - 4} textAnchor="start">
          {when(at0.ts)}
        </text>
        <text className="elo-chart-tick" x={W - RIGHT} y={H - 4} textAnchor="end">
          {when(last.ts)}
        </text>
      </svg>

      {/* Doubles as the legend. The cursor rests on the newest game, so what it
          reads with nobody touching it is the state of things now. */}
      <div className="elo-chart-readout">
        <span className="elo-chart-when">
          {here.matchId === null ? 'start' : `${when(here.ts)} · game ${cursor}`}
        </span>
        <span className="elo-chart-value num">{display(here.elo)}</span>
        {here.matchId !== null && (
          <span className={here.delta >= 0 ? 'delta up' : 'delta down'}>
            {here.delta >= 0 ? '+' : ''}
            {Math.round(here.delta)}
          </span>
        )}
        {here.placement && <span className="pip">placement</span>}
      </div>
    </div>
  );

  function scrub(clientX: number, target: SVGSVGElement) {
    const box = target.getBoundingClientRect();
    const fraction = (clientX - box.left) / box.width;
    setAt(Math.round(Math.min(1, Math.max(0, fraction)) * (series.length - 1)));
  }
}

/** Short enough for an axis, unambiguous enough to place a game by. */
function when(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
